import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Grid, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUILoadingLinear } from '../../atomic/atoms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import {
    StoreCustomerManagementCustomer,
    StoreCustomerManagementQuery,
} from '../../services/query/storeCustomerManagement/StoreCustomerManagementQuery.services';
import { CustomerCardPaper } from '../CustomerManagement/components/CustomerCardPaper/CustomerCardPaper';
import { CustomerMenuPaper } from '../CustomerManagement/components/CustomerMenuPaper/CustomerMenuPaper';
import {
    normalizeTurkishPhoneForApi,
    STORE_CUSTOMER_SEARCH_TYPE,
    StoreCustomerPhoneSearchForm,
    StoreCustomerSearchParams,
} from './storeCustomerPhoneSearch.shared';
import {
    buildStoreCustomerQuery,
    DEFAULT_STORE_CUSTOMER_ACTIVE_TAB,
    resolveStoreCustomerMenuIdFromActiveTab,
    STORE_CUSTOMER_ACTIVE_TAB_MANAGEMENT,
    STORE_CUSTOMER_ACTIVE_TAB_ORDERS,
    STORE_CUSTOMER_MENU_MANAGEMENT,
    STORE_CUSTOMER_MENU_ORDERS,
} from './storeCustomerTabs.constants';
import { mapGlobalCustomerToCardDatas } from './mapGlobalCustomerToCardDatas';

export {
    STORE_CUSTOMER_MENU_MANAGEMENT,
    STORE_CUSTOMER_MENU_ORDERS,
} from './storeCustomerTabs.constants';

type StoreCustomerShellContextValue = {
    globalCustomer: StoreCustomerManagementCustomer | null;
    memberDatas: ReturnType<typeof mapGlobalCustomerToCardDatas> | null;
    customerId: number;
    searched: boolean;
    searchLoading: boolean;
    refreshMemberDatas: () => void;
};

const StoreCustomerShellContext = createContext<StoreCustomerShellContextValue | null>(null);

export const useStoreCustomerShell = (): StoreCustomerShellContextValue => {
    const ctx = useContext(StoreCustomerShellContext);
    if (!ctx) {
        throw new Error('useStoreCustomerShell must be used within StoreCustomerShell');
    }
    return ctx;
};

const getPhoneFromGlobalCustomer = (global: StoreCustomerManagementCustomer): string | null => {
    const phone = global.customerPhones?.[0]?.phone;
    return phone?.trim() ? phone : null;
};

type StoreCustomerShellProps = {
    children: ReactNode;
};

export const StoreCustomerShell: FC<StoreCustomerShellProps> = ({ children }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const searchParams = new URLSearchParams(location.search);

    const [globalCustomer, setGlobalCustomer] = useState<StoreCustomerManagementCustomer | null>(null);
    const [searched, setSearched] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [tabIndex, setTabIndex] = useState(STORE_CUSTOMER_MENU_MANAGEMENT);
    const [refreshToken, setRefreshToken] = useState(false);

    const activeTabParam = searchParams.get('ActiveTab');

    const customerId = globalCustomer?.ssoCustomerId ?? 0;

    const memberDatas = useMemo(
        () => (globalCustomer ? mapGlobalCustomerToCardDatas(globalCustomer) : null),
        [globalCustomer]
    );

    const refreshGlobalCustomerByPhone = useCallback(async (global: StoreCustomerManagementCustomer) => {
        const phoneRaw = getPhoneFromGlobalCustomer(global);
        if (!phoneRaw) return;
        const phoneForRequest = normalizeTurkishPhoneForApi(phoneRaw);
        const res = await StoreCustomerManagementQuery.searchCustomerByPhone({ phone: phoneForRequest });
        if (res?.data) {
            setGlobalCustomer(res.data);
        }
    }, []);

    useEffect(() => {
        if (refreshToken && globalCustomer) {
            void refreshGlobalCustomerByPhone(globalCustomer);
        }
    }, [refreshToken, globalCustomer, refreshGlobalCustomerByPhone]);

    useEffect(() => {
        setTabIndex(resolveStoreCustomerMenuIdFromActiveTab(activeTabParam));
    }, [activeTabParam]);

    useEffect(() => {
        if (globalCustomer && !activeTabParam) {
            history.replace(buildStoreCustomerQuery(DEFAULT_STORE_CUSTOMER_ACTIVE_TAB));
        }
    }, [activeTabParam, globalCustomer, history]);

    const applySearchResult = useCallback(
        (customer: StoreCustomerManagementCustomer | undefined) => {
            if (!customer?.ssoCustomerId) {
                setSearched(true);
                return;
            }
            setGlobalCustomer(customer);
            setSearched(true);
            history.replace(
                buildStoreCustomerQuery(activeTabParam ?? DEFAULT_STORE_CUSTOMER_ACTIVE_TAB)
            );
        },
        [activeTabParam, history]
    );

    const handleSearch = useCallback(
        ({ searchType, value }: StoreCustomerSearchParams) => {
            setSearchLoading(true);
            setGlobalCustomer(null);
            setSearched(false);

            const requestPromise =
                searchType === STORE_CUSTOMER_SEARCH_TYPE.customerId
                    ? StoreCustomerManagementQuery.searchCustomerBySsoId({
                          customerId: Number(value),
                      })
                    : StoreCustomerManagementQuery.searchCustomerByPhone({
                          phone: normalizeTurkishPhoneForApi(value),
                      });

            requestPromise
                .then((res) => {
                    applySearchResult(res?.data as StoreCustomerManagementCustomer | undefined);
                })
                .catch(() => {
                    Swal.fire(t('general.error'), t('general.anErrorOccured'), 'error');
                    setSearched(true);
                })
                .finally(() => {
                    setSearchLoading(false);
                });
        },
        [applySearchResult, t]
    );

    const tabList = useMemo(() => {
        if (!globalCustomer) return [];
        return [
            {
                id: STORE_CUSTOMER_MENU_MANAGEMENT,
                name: t('storeCustomerManagement.sideMenuLabel'),
                link: buildStoreCustomerQuery(STORE_CUSTOMER_ACTIVE_TAB_MANAGEMENT),
                code: STORE_CUSTOMER_ACTIVE_TAB_MANAGEMENT,
                isShow: true,
            },
            {
                id: STORE_CUSTOMER_MENU_ORDERS,
                name: t('storeCustomerOrderManagement.sideMenuLabel'),
                link: buildStoreCustomerQuery(STORE_CUSTOMER_ACTIVE_TAB_ORDERS),
                code: STORE_CUSTOMER_ACTIVE_TAB_ORDERS,
                isShow: true,
            },
        ];
    }, [globalCustomer, t]);

    useEffect(() => {
        if (!globalCustomer || tabList.length === 0) return;
        const tabCode = activeTabParam ?? DEFAULT_STORE_CUSTOMER_ACTIVE_TAB;
        const matchingTab = tabList.find((tab) => tab.code === tabCode);
        if (matchingTab) {
            setTabIndex(matchingTab.id);
        }
    }, [activeTabParam, globalCustomer, tabList]);

    const onClickBreadCrumb = () => {
        setGlobalCustomer(null);
        setSearched(false);
        history.replace(PageUrl.storeCustomerManagement.url);
    };

    const refreshMemberDatas = () => setRefreshToken((v) => !v);

    const contextValue = useMemo<StoreCustomerShellContextValue>(
        () => ({
            globalCustomer,
            memberDatas,
            customerId,
            searched,
            searchLoading,
            refreshMemberDatas,
        }),
        [globalCustomer, memberDatas, customerId, searched, searchLoading]
    );

    const showSideMenu = globalCustomer != null;
    const showSearchForm = !showSideMenu;

    return (
        <StoreCustomerShellContext.Provider value={contextValue}>
            <SCUIContent>
                <SCUILoadingLinear display={searchLoading} />
                <Grid
                    container
                    direction="row"
                    spacing={2}
                    sx={{ marginLeft: '0.5rem', marginTop: '0.1rem' }}
                >
                    <SCUIBreadCrumb
                        title={t('storeCustomerManagement.title')}
                        componentLink={PageUrl.storeCustomerManagement.url}
                        onClick={onClickBreadCrumb}
                    />

                    <Grid sx={{ marginLeft: '0.5rem' }} size={{ xl: 12, lg: 12, md: 12, xs: 12 }}>
                        {showSearchForm && (
                            <>
                                <StoreCustomerPhoneSearchForm
                                    searchLoading={searchLoading}
                                    phoneLabel={t('storeCustomerManagement.phoneNumberLabel')}
                                    customerIdLabel={t('storeCustomerManagement.customerIdLabel')}
                                    searchAriaLabel={t('storeCustomerManagement.searchAriaLabel')}
                                    onSearch={handleSearch}
                                />

                                {searched && !searchLoading && !globalCustomer && (
                                    <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                                        {t('storeCustomerManagement.noCustomerFound')}
                                    </Typography>
                                )}
                            </>
                        )}

                        {showSideMenu && memberDatas && (
                            <Grid container direction="row" sx={{ marginLeft: '0.5rem', mt: 2 }}>
                                <Grid
                                    sx={{ marginBottom: '1rem' }}
                                    size={{ xl: 12, lg: 12, md: 12, xs: 12 }}
                                >
                                    <CustomerCardPaper
                                        datas={memberDatas}
                                        refresh={refreshMemberDatas}
                                        hidePhoneManagement
                                    />
                                </Grid>
                                <Grid size={{ xl: 2, lg: 3, md: 12, xs: 12 }}>
                                    <CustomerMenuPaper
                                        tabList={tabList}
                                        tIndex={tabIndex}
                                        setTabIndex={setTabIndex}
                                        setIndex={setTabIndex}
                                    />
                                </Grid>
                                <Grid sx={{ paddingLeft: '1rem' }} size={{ xl: 9.5, lg: 9, md: 12, xs: 12 }}>
                                    {children}
                                </Grid>
                            </Grid>
                        )}

                    </Grid>
                </Grid>
            </SCUIContent>
        </StoreCustomerShellContext.Provider>
    );
};
